import React, { useEffect, useMemo, useState } from "react";
import { Building2, Grid2x2, Users, DollarSign } from "lucide-react";
import {
  getSuperAdminDashboard,
  getSuperAdminReport,
  SuperAdminDashboardData,
  SuperAdminReportData,
} from "../../services/superAdminApi";

const cardCls =
  "bg-white rounded-2xl shadow-sm border border-gray-200 p-5";

const SuperAdminDashboard: React.FC = () => {
  const [data, setData] = useState<SuperAdminDashboardData | null>(null);
  const [reportData, setReportData] = useState<SuperAdminReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportError, setReportError] = useState("");

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const thirtyDaysAgo = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().slice(0, 10);
  }, []);
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);

  const loadDashboard = async (start: string, end: string) => {
    const res = await getSuperAdminDashboard(start, end);
    setData(res);
  };

  const loadReport = async (start: string, end: string) => {
    try {
      setReportLoading(true);
      setReportError("");
      const report = await getSuperAdminReport(start, end);
      setReportData(report);
    } catch (e: any) {
      setReportError(e?.response?.data?.message || "Failed to load date-range report.");
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        await loadDashboard(startDate, endDate);
        await loadReport(startDate, endDate);
      } catch (e: any) {
        setError(e?.response?.data?.message || "Failed to load superadmin dashboard.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div className="p-6 text-gray-600">Loading superadmin dashboard...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  const currentRangeLabel = reportData
    ? `${reportData.range.start_date} to ${reportData.range.end_date}`
    : data?.range
      ? `${data.range.start_date} to ${data.range.end_date}`
      : `${startDate} to ${endDate}`;

  const webCards = [
    {
      label: "Active Companies",
      value: data?.cards?.active_companies ?? data?.companies.active ?? 0,
      icon: Building2,
      iconWrap: "bg-green-500",
    },
    {
      label: "Inactive Companies",
      value: data?.cards?.inactive_companies ?? data?.companies.inactive ?? 0,
      icon: Building2,
      iconWrap: "bg-red-500",
    },
    {
      label: "Total Categories",
      value: data?.cards?.total_categories ?? 0,
      icon: Grid2x2,
      iconWrap: "bg-cyan-500",
    },
    {
      label: "Total Customers",
      value: data?.cards?.total_customers ?? 0,
      icon: Users,
      iconWrap: "bg-gray-800",
    },
    {
      label: "Total Commission",
      value: (data?.cards?.total_commission ?? 0).toFixed(2),
      icon: DollarSign,
      iconWrap: "bg-amber-400",
    },
    {
      label: "Total Sales",
      value: (data?.cards?.total_sales ?? 0).toFixed(2),
      icon: DollarSign,
      iconWrap: "bg-blue-500",
    },
  ];

  return (
    <div className="space-y-5 p-1">
      <div>
        <h1 className="text-2xl font-semibold text-[#402B69]">Superadmin Dashboard</h1>
        <p className="text-sm text-gray-500">Platform-level overview with business health and date-range analytics.</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <button
          onClick={async () => {
            await loadDashboard(startDate, endDate);
            await loadReport(startDate, endDate);
          }}
          disabled={reportLoading}
          className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-70"
        >
          {reportLoading ? "Applying..." : "Apply Date Range"}
        </button>
        <p className="text-xs text-gray-500 ml-auto">Current range: {currentRangeLabel}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {webCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className={`${cardCls} flex items-center gap-4`}>
              <div className={`flex h-12 w-12 items-center justify-center rounded-full ${card.iconWrap}`}>
                <Icon size={22} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">{card.label}</p>
                <p className="text-3xl font-bold leading-tight text-[#1E1E1E]">{card.value}</p>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default SuperAdminDashboard;

